<template>
  <b-modal
    v-model="open"
    :title="modalTitle"
    ok-only
    ok-title="OK"
    ok-variant="primary"
    centered
    :size="modalSize"
    modal-class="tradie-csv-import-error-modal"
    :header-bg-variant="headerBgVariant"
    header-text-variant="dark"
    :header-border-variant="headerBgVariant"
    body-class="pt-3 pb-2"
    footer-class="border-0 pt-0"
    content-class="shadow"
  >
    <div class="tradie-csv-import-error">
      <p class="summary-text text-dark mb-0">{{ summary }}</p>

      <b-alert
        v-if="showImportDetailsExplainer"
        show
        variant="light"
        class="small border mt-3 mb-0 py-2"
      >
        {{ importDetailsExplainerText }}
      </b-alert>

      <template v-if="importedEmails.length">
        <hr class="my-3" />
        <p class="font-weight-bold text-success mb-2">Imported</p>
        <div
          class="import-csv-error-emails-wrap border rounded border-success mb-3"
        >
          <ul class="import-csv-error-emails list-unstyled mb-0 py-2 px-2">
            <li
              v-for="(email, i) in importedEmails"
              :key="'ok-' + i"
              class="py-2 px-2 mb-1 rounded bg-light border border-light"
            >
              <span class="text-monospace">{{ email }}</span>
            </li>
          </ul>
        </div>
      </template>

      <template v-if="hasDetailLists">
        <hr class="my-3" />

        <template v-if="skippedAlreadyInCompany.length">
          <p class="font-weight-bold mb-2">Already in this company</p>
          <p class="small text-muted mb-2">
            Remove these addresses from your CSV before you upload again if you
            want a file that only contains people who are not already on this
            company. Leaving them in will keep them showing as skipped.
          </p>
          <div class="import-csv-error-emails-wrap border rounded mb-4">
            <ul class="import-csv-error-emails list-unstyled mb-0 py-2 px-2">
              <li
                v-for="(email, i) in skippedAlreadyInCompany"
                :key="'in-co-' + i"
                class="py-2 px-2 mb-1 rounded bg-light border border-light"
              >
                <span class="text-monospace">{{ email }}</span>
              </li>
            </ul>
          </div>
        </template>

        <template v-if="invitedEmails.length">
          <p class="font-weight-bold mb-2">Tradies invited to company</p>
          <p class="small text-muted mb-2">
            These people already had an account; an invite was sent. Remove them
            from your CSV before the next upload if you want a file that only
            lists people you still need to add.
          </p>
          <div class="import-csv-error-emails-wrap border rounded mb-4">
            <ul class="list-unstyled invited-to-company-list mb-0 py-2 px-2">
              <li
                v-for="(email, i) in invitedEmails"
                :key="'inv-' + i"
                class="py-2 px-2 mb-1 rounded bg-light border border-light"
              >
                <span class="text-monospace">{{ email }}</span>
              </li>
            </ul>
          </div>
        </template>

        <template v-if="skippedBusinessAdmins.length">
          <p class="font-weight-bold mb-2">Business admin accounts</p>
          <div class="import-csv-error-emails-wrap border rounded mb-4">
            <ul class="import-csv-error-emails list-unstyled mb-0 py-2 px-2">
              <li
                v-for="(email, i) in skippedBusinessAdmins"
                :key="'ba-' + i"
                class="py-2 px-2 mb-1 rounded bg-light border border-light"
              >
                <span class="text-monospace">{{ email }}</span>
              </li>
            </ul>
          </div>
        </template>

        <template v-if="emails.length">
          <p class="font-weight-bold mb-2">Already registered</p>
          <div class="import-csv-error-emails-wrap border rounded">
            <ul class="import-csv-error-emails list-unstyled mb-0 py-2 px-2">
              <li
                v-for="(email, i) in emails"
                :key="'reg-' + i"
                class="py-2 px-2 mb-1 rounded bg-light border border-light"
              >
                <span class="text-monospace">{{ email }}</span>
              </li>
            </ul>
          </div>
        </template>

        <p class="small text-muted mt-3 mb-0">{{ footerHint }}</p>
      </template>

      <p v-else-if="hasOutcomeLists" class="small text-muted mt-3 mb-0">
        {{ footerOutcomeOnly }}
      </p>

      <p v-else class="small text-muted mb-0">{{ fallbackHint }}</p>
    </div>
  </b-modal>
</template>

<script>
import {
  CSV_IMPORT_MSG_INVITES_ONLY,
  CSV_IMPORT_MSG_WITH_SKIPS,
} from '@/constants/companyTradieMessages'

export default {
  name: 'TradieCsvImportErrorModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    /** When `partial_success`, title and styling reflect a completed import with warnings. */
    variant: {
      type: String,
      default: 'failure',
      validator: (v) => ['failure', 'partial_success'].includes(v),
    },
    summary: {
      type: String,
      default: '',
    },
    /** Legacy project import: emails already registered on the platform. */
    emails: {
      type: Array,
      default: () => [],
    },
    skippedAlreadyInCompany: {
      type: Array,
      default: () => [],
    },
    skippedBusinessAdmins: {
      type: Array,
      default: () => [],
    },
    /** When the API returns `imported_emails` (optional). */
    importedEmails: {
      type: Array,
      default: () => [],
    },
    /** `invited_existing_accounts` from CSV import API (existing users, invite only). */
    invitedEmails: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    open: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
    modalTitle() {
      if (this.variant !== 'partial_success') return 'Import failed'
      const s = (this.summary || '').trim()
      if (s === CSV_IMPORT_MSG_INVITES_ONLY) return 'Invitations sent'
      if (s === CSV_IMPORT_MSG_WITH_SKIPS)
        return 'Import completed with warnings'
      return 'Import completed'
    },
    headerBgVariant() {
      return 'warning'
    },
    hasSkipLists() {
      return (
        this.skippedAlreadyInCompany.length > 0 ||
        this.skippedBusinessAdmins.length > 0 ||
        this.emails.length > 0
      )
    },
    /** Skips, invites, or legacy rows — anything in the detail section below the summary. */
    hasDetailLists() {
      return this.hasSkipLists || this.invitedEmails.length > 0
    },
    showImportDetailsExplainer() {
      return this.variant === 'failure' && this.hasDetailLists
    },
    importDetailsExplainerText() {
      const hasSkips = this.hasSkipLists
      const hasInv = this.invitedEmails.length > 0
      if (hasSkips && hasInv) {
        return 'The sections below list skipped rows and invites. Remove those emails from your CSV before you upload again if you want a smaller file that only includes people you still need to process.'
      }
      if (hasInv) {
        return 'Remove the emails below from your CSV before the next upload if you do not want them listed here again. They are not on your tradie list until they accept the invite.'
      }
      return 'Remove the emails below from your CSV before you upload again if you want to avoid seeing them as skipped. Other rows from your file are not listed here.'
    },
    totalListedEmails() {
      return (
        this.importedEmails.length +
        this.invitedEmails.length +
        this.skippedAlreadyInCompany.length +
        this.skippedBusinessAdmins.length +
        this.emails.length
      )
    },
    hasOutcomeLists() {
      return this.importedEmails.length > 0 || this.invitedEmails.length > 0
    },
    footerOutcomeOnly() {
      const parts = []
      if (this.importedEmails.length) {
        parts.push(
          this.variant === 'partial_success'
            ? 'New tradies from this upload appear in your list. Remove any rows you do not need in the file before the next import.'
            : 'New tradies from this upload should appear in your list after you refresh.',
        )
      }
      if (this.invitedEmails.length) {
        parts.push(
          'Remove those addresses from your CSV next time if you only want new rows in the file.',
        )
      }
      return parts.join(' ')
    },
    modalSize() {
      return this.totalListedEmails > 6 ? 'lg' : 'md'
    },
    footerHint() {
      if (this.variant === 'partial_success') {
        return 'New accounts from this upload appear in your list. To tidy your CSV for next time, remove the emails in the sections above-otherwise they will be skipped or invited again when included in the file.'
      }
      if (
        this.emails.length &&
        !this.skippedAlreadyInCompany.length &&
        !this.skippedBusinessAdmins.length
      ) {
        return 'Remove those rows from your CSV and upload again. Nothing was imported from this file.'
      }
      return 'Nothing was imported from this file. Adjust your CSV if you need those addresses included.'
    },
    fallbackHint() {
      return this.variant === 'partial_success'
        ? 'Imported tradies appear in your list.'
        : 'Fix the issue above and try again. Nothing was imported from this file.'
    },
  },
}
</script>

<style scoped>
.import-csv-error-emails-wrap {
  max-height: min(50vh, 280px);
  overflow-y: auto;
  background: #fafafa;
}

.summary-text {
  font-size: 1rem;
  line-height: 1.55;
  white-space: pre-wrap;
}
</style>
